import { useId } from "react";

/**
 * Знак IEC — глобус с двумя зелёными дугами (`assets/mark-globe.svg` макета).
 *
 * Встроен в разметку, а не подключён `<img>`: знак стоит первым элементом
 * карточки входа, и отдельный сетевой запрос за ним даёт заметный «прыжок»
 * ровно в тот момент, когда человек читает заголовок.
 *
 * Идентификаторы масок разведены через `useId` — иначе два знака на одной
 * странице склеили бы маски и один из них потерял бы дуги.
 */
export function BrandMark({ size = 48, title }: { size?: number; title?: string }) {
	const id = useId();
	const inside = `${id}-in`;
	const outside = `${id}-out`;

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 64 64"
			fill="none"
			role={title ? "img" : "presentation"}
			aria-label={title}
			aria-hidden={title ? undefined : true}
			className="block"
		>
			<defs>
				<mask id={inside}>
					<rect width="100%" height="100%" fill="#000" />
					<circle cx="32" cy="32" r="19.2" fill="#fff" />
				</mask>
				<mask id={outside}>
					<rect width="100%" height="100%" fill="#fff" />
					<circle cx="32" cy="32" r="19.2" fill="#000" />
				</mask>
			</defs>
			<circle cx="32" cy="32" r="19.2" fill="currentColor" />
			<g mask={`url(#${inside})`}>
				<path
					d="M9.27 40.27Q37.60 44.01 52.95 19.90"
					stroke="var(--p-green)"
					strokeWidth="3.84"
					strokeLinecap="round"
					fill="none"
				/>
				<path
					d="M50.53 47.55Q25.66 37.71 18.47 11.94"
					stroke="var(--p-green)"
					strokeWidth="3.84"
					strokeLinecap="round"
					fill="none"
				/>
			</g>
			<g mask={`url(#${outside})`}>
				<path
					d="M9.27 40.27Q37.60 44.01 52.95 19.90"
					stroke="currentColor"
					strokeWidth="3.84"
					strokeLinecap="round"
					fill="none"
				/>
				<path
					d="M50.53 47.55Q25.66 37.71 18.47 11.94"
					stroke="currentColor"
					strokeWidth="3.84"
					strokeLinecap="round"
					fill="none"
				/>
			</g>
			<circle cx="9.27" cy="40.27" r="3.2" fill="currentColor" />
			<circle cx="52.95" cy="19.90" r="3.2" fill="currentColor" />
			<circle cx="50.53" cy="47.55" r="3.2" fill="currentColor" />
			<circle cx="18.47" cy="11.94" r="3.2" fill="currentColor" />
		</svg>
	);
}

/**
 * Фирменный знак Google для кнопки входа. Четыре сектора — фиксированные
 * цвета бренда Google: это чужая айдентика, перекрашивать её нельзя, поэтому
 * hex здесь не нарушение токенов, а требование гайдлайна Google.
 */
export function GoogleMark({ size = 20 }: { size?: number }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 48 48"
			aria-hidden="true"
			className="block"
		>
			<path
				fill="#EA4335"
				d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.4z"
			/>
			<path
				fill="#FBBC05"
				d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.9-6.2z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"
			/>
		</svg>
	);
}
